import React, { Fragment } from "react";
import classNames from "classnames";

import PropTypes from "prop-types";
import Loader from "../Loader/Loader";
import Tooltip from "../Tooltip/Tooltip";

import "./HeaderGrid.scss";

const HeaderGrid = props => {
	const { gridOptions, counts, loadingCounts, activeGrid, setActive, showTooltip, showCounts } =
		props;

	/**
	 * Handles changes to the active grid selection on mobile devices (a select menu is used instead of the grid shown on desktop)
	 * @param {Event} event The HTML event handled by this method
	 */
	const handleSelectChange = event => {
		const { value } = event.target;
		if (value !== activeGrid.toString()) {
			setActive(value);
		}
	};

	/**
	 * Handles changes to the active grid selection based on the options passed to this component
	 * @param {Number|String} type Indicates which grid should be set to active
	 */
	const handleChange = type => {
		if (type !== activeGrid) setActive(type);
	};

	return (
		<Fragment>
			<div
				className="visible-xs"
				id="header-select"
			>
				<select
					name="selectOption"
					className="form-control"
					value={activeGrid}
					onChange={handleSelectChange}
				>
					<For
						each="item"
						of={gridOptions}
						index="index"
					>
						<option
							key={`item_${item.countKey}`}
							value={item.type}
						>
							{item.displayName}
							<If condition={showCounts}>
								&nbsp;
								{`(${counts[item.countKey] || 0})`}
							</If>
						</option>
					</For>
				</select>
			</div>

			<div
				className="header-grid hidden-xs"
				id="header-grid"
			>
				<For
					each="item"
					of={gridOptions}
					index="index"
				>
					<div
						role="button"
						tabIndex="0"
						key={`header_grid${item.countKey}`}
						className={`grid-item ${item.type === activeGrid ? "active" : ""} ${
							!Object.keys(counts).includes(item.countKey) ? "align-column" : ""
						}`}
						onClick={() => handleChange(item.type)}
						onKeyPress={event => {
							if (event.key === "Enter") handleChange(item.type);
						}}
						data-test-id={item.dataTestId}
					>
						<div
							className={classNames({
								"flex flex-direction-column flex-justify-content-space-between height-100":
									Object.keys(counts).includes(item.countKey),
							})}
						>
							<span title={item.displayName}>{item.displayName}</span>
							<If condition={item.description}>
								<If condition={showTooltip}>
									<div className="grid-help-icon hidden-xs">
										<Tooltip content={item.description}>
											<i
												className={`rkf help-icon ${
													item.type === activeGrid
														? "rkf-question-solid"
														: "rkf-question-o"
												}`}
											/>
										</Tooltip>
									</div>
								</If>
							</If>
							<If condition={Object.keys(counts).length > 0}>
								<Choose>
									<When condition={loadingCounts && item.type !== "paid"}>
										<div className="grid-loader">
											<Loader
												active
												size="small"
												spinnerOnly
											/>
										</div>
									</When>
									<Otherwise>
										<h3 className={`${item.type === "paid" ? "hidden" : ""}`}>
											{counts[item.countKey] || 0}
										</h3>
									</Otherwise>
								</Choose>
							</If>
						</div>
					</div>
				</For>
			</div>
		</Fragment>
	);
};

HeaderGrid.propTypes = {
	gridOptions: PropTypes.array.isRequired,
	activeGrid: PropTypes.any.isRequired,
	setActive: PropTypes.func.isRequired,
	counts: PropTypes.object,
	loadingCounts: PropTypes.bool,
	showTooltip: PropTypes.bool,
	showCounts: PropTypes.bool,
};

HeaderGrid.defaultProps = {
	counts: {},
	loadingCounts: false,
	showTooltip: false,
	showCounts: true,
};

export default HeaderGrid;
